import React from 'react';

import { Person, Schedule } from '@mui/icons-material';
import { Box, Card, InputAdornment, Stack, Typography, useTheme } from '@mui/material';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { clamp, get, isEmpty, isNil } from 'lodash';
import { Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { CardLevel, ContainerFlexHeight, EnlargeableAvatar, LoadingButton, NumberTextField } from '../../../components';
import TimePickerWithShorthand from '../../../components/time_picker_with_shorthand';
import { createScopedI18n } from '../../../i18n/i18n';
import { calculateDurationMinute, formatTime, joinPairs, syncTimeToDate } from '../../../utils/libs';
import snackbar, { SAVE_SUCCESS } from '../../../utils/snackbar';
import { AttendancesPageContext } from '../page/attendances_page_context';
import StatusChip from '../page/components/status_chip';
import useApproveAttendancesMutation from '../page/hooks/use_approve_attendances_mutation';
import useAttendanceEditForm from '../page/hooks/use_attendance_edit_form';
import useAttendanceLocation from '../page/hooks/use_attendance_location';
import useRejectAttendancesMutation from '../page/hooks/use_reject_attendances_mutation';
import useUpdateAttendancesMutation from '../page/hooks/use_update_attendances_mutation';

import type { AttendanceEditFormTransformedValues } from '../page/hooks/use_attendance_edit_form';
import type { JobHiringAttendance } from '../page/type';

const attendancesDetailPageI18n = createScopedI18n('pages.attendances_id');

type AttendancesDetailPageProps = {
  attendance: JobHiringAttendance | null;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  selectAttendance?: (attendanceId: string, selected: boolean) => void;
};

const AttendancesDetailPage = React.memo(
  ({ attendance, setHasUnsavedChanges, selectAttendance }: AttendancesDetailPageProps) => {
    const theme = useTheme();

    const [pickerKey, setPickerKey] = React.useState<string>(attendance?.id ?? '');

    const {
      control,
      handleSubmit,
      getValues,
      setValue,
      reset,
      watch,
      formState: { isDirty },
    } = useAttendanceEditForm({
      defaultValues: {
        startOperationTime: attendance?.startOperationTime ?? null,
        endOperationTime: attendance?.endOperationTime ?? null,
        breakDurationMinute: attendance?.breakDurationMinute ?? null,
      },
    });

    React.useEffect(() => {
      reset({
        startOperationTime: attendance?.startOperationTime ?? null,
        endOperationTime: attendance?.endOperationTime ?? null,
        breakDurationMinute: attendance?.breakDurationMinute ?? null,
      });
      // Update formKey in the same effect so TimePickerWithShorthand remounts
      // only after reset() has already set the correct form values synchronously.
      setPickerKey(attendance?.id ?? '');
    }, [attendance, reset]);

    // Sync form dirty state with detail page dirty flag
    React.useEffect(() => {
      setHasUnsavedChanges(isDirty);
    }, [isDirty]);

    const job = attendance?.jobHiring?.job;
    const student = attendance?.jobHiring?.student;
    const editable = attendance?.status === 'pending';

    const jobOtHour = job?.otHour ?? null;
    const jobDurationMinutes = React.useMemo(() => {
      if (isNil(job?.jobDurationMinute)) {
        return calculateDurationMinute(job?.jobStartAt, job?.jobEndAt, job?.breakDurationMinute);
      }
      return job?.jobDurationMinute ?? null;
    }, [job?.jobDurationMinute, job?.jobStartAt, job?.jobEndAt, job?.breakDurationMinute]);
    const jobDurationHours = isNil(jobDurationMinutes) ? null : jobDurationMinutes / 60.0;

    const startOperationTime = watch('startOperationTime');
    const endOperationTime = watch('endOperationTime');
    const breakDurationMinute = watch('breakDurationMinute');

    const operationDurationMinutes = React.useMemo(
      () => calculateDurationMinute(startOperationTime, endOperationTime, breakDurationMinute),
      [startOperationTime, endOperationTime, breakDurationMinute],
    );
    const operationDurationHours = isNil(operationDurationMinutes) ? null : operationDurationMinutes / 60.0;
    const operationDurationOtHours = React.useMemo(() => {
      if (isNil(jobOtHour) || isNil(operationDurationHours)) return null;
      return clamp(operationDurationHours - jobOtHour, 0, operationDurationHours);
    }, [jobOtHour, operationDurationHours]);

    const { isStartOperationInRange, isEndOperationInRange } = useAttendanceLocation(attendance);

    const timeChangeHandler = React.useCallback(
      (name: 'startOperationTime' | 'endOperationTime', time: Dayjs | null) => {
        if (isNil(time)) {
          setValue(name, null, { shouldDirty: true });
          return;
        }

        const isEnd = name === 'endOperationTime';
        const targetDate = isEnd ? job?.jobEndAt : job?.jobStartAt;
        const startTime = isEnd ? getValues('startOperationTime') : undefined;

        const newTime = syncTimeToDate(time, targetDate, startTime);
        setValue(name, newTime, { shouldDirty: true });
      },
      [job?.jobEndAt, job?.jobStartAt, setValue, getValues],
    );

    const {
      approveAttendances,
      result: { loading: approveLoading },
    } = useApproveAttendancesMutation();
    const {
      rejectAttendances,
      result: { loading: rejectLoading },
    } = useRejectAttendancesMutation();
    const {
      updateAttendances,
      result: { loading: updateLoading },
    } = useUpdateAttendancesMutation();

    const isSubmitting = approveLoading || rejectLoading || updateLoading;
    const [actionErrorMessage, setActionErrorMessage] = React.useState<string | null>(null);
    const [updateErrorMessage, setUpdateErrorMessage] = React.useState<string | null>(null);

    const approveAttendanceHandler = React.useCallback(async () => {
      if (!attendance?.id) return;
      if (!editable) return;

      setActionErrorMessage(null);

      const result = await approveAttendances([{ id: attendance.id }]);

      if (result.success) {
        selectAttendance?.(attendance.id, false);
      } else if (result.errors || result.error) {
        const nextErrorMessage = result.errors
          ? joinPairs(result.errors)
          : get(result.error, 'message', 'Unknown error');
        setActionErrorMessage(nextErrorMessage);
      }
    }, [attendance, editable, approveAttendances, selectAttendance]);

    const rejectAttendanceHandler = React.useCallback(async () => {
      if (!attendance?.id) return;
      if (!editable) return;

      setActionErrorMessage(null);

      const result = await rejectAttendances([{ id: attendance.id }]);

      if (result.success) {
        selectAttendance?.(attendance.id, false);
      } else if (result.errors || result.error) {
        const nextErrorMessage = result.errors
          ? joinPairs(result.errors)
          : get(result.error, 'message', 'Unknown error');
        setActionErrorMessage(nextErrorMessage);
      }
    }, [attendance, editable, rejectAttendances, selectAttendance]);

    const editAttendanceHandler = React.useCallback(
      async (data: AttendanceEditFormTransformedValues) => {
        if (!attendance?.id) return;
        if (!editable) return;

        setUpdateErrorMessage(null);
        const newAttendance = {
          id: attendance.id,
          startOperationTime: data.startOperationTime ? dayjs(data.startOperationTime).toISOString() : null,
          endOperationTime: data.endOperationTime ? dayjs(data.endOperationTime).toISOString() : null,
          breakDurationMinute: data.breakDurationMinute,
        };

        const result = await updateAttendances([newAttendance]);

        if (result.success) {
          snackbar.enqueueSuccess(SAVE_SUCCESS);
          reset({
            startOperationTime: newAttendance.startOperationTime ? dayjs(newAttendance.startOperationTime) : null,
            endOperationTime: newAttendance.endOperationTime ? dayjs(newAttendance.endOperationTime) : null,
            breakDurationMinute: newAttendance.breakDurationMinute,
          });
        } else if (result.errors || result.error) {
          const nextErrorMessage = result.errors
            ? joinPairs(result.errors)
            : get(result.error, 'message', 'Unknown error');
          setUpdateErrorMessage(nextErrorMessage);
        }
      },
      [attendance, editable, updateAttendances],
    );

    if (isEmpty(attendance)) {
      return (
        <Box padding={2}>
          <CardLevel variant="outlined" level={1} sx={{ width: '100%' }}>
            <Stack justifyContent="center" alignItems="center" padding={2}>
              <Typography variant="caption" textAlign="center" sx={{ opacity: 0.6 }}>
                {attendancesDetailPageI18n('no_records')}
              </Typography>
            </Stack>
          </CardLevel>
        </Box>
      );
    }

    return (
      <ContainerFlexHeight maxWidth="md" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack gap={1} flexDirection="row" justifyContent="flex-end" alignItems="baseline">
          {editable ? (
            <>
              {isDirty ? (
                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                  * {attendancesDetailPageI18n('unsaved_changes')}
                </Typography>
              ) : null}
              <Stack gap={0.5} alignItems="flex-end">
                <Stack flexDirection="row" gap={1}>
                  <LoadingButton
                    variant="outlined"
                    color="error"
                    disabled={isDirty || isSubmitting}
                    loading={rejectLoading}
                    onClick={rejectAttendanceHandler}
                  >
                    {attendancesDetailPageI18n('reject_button')}
                  </LoadingButton>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    disabled={isDirty || isSubmitting}
                    loading={approveLoading}
                    onClick={approveAttendanceHandler}
                  >
                    {attendancesDetailPageI18n('approve_button')}
                  </LoadingButton>
                </Stack>
                {actionErrorMessage ? (
                  <Typography variant="caption" color="error">
                    {actionErrorMessage}
                  </Typography>
                ) : null}
              </Stack>
            </>
          ) : (
            <StatusChip status={attendance.status} />
          )}
        </Stack>

        <Stack gap={2} flexDirection="row" alignItems="center">
          <Stack width={120} maxWidth="50%" sx={{ aspectRatio: 1 }}>
            <EnlargeableAvatar src={student?.avatar ?? ''} fullWidth fullHeight />
          </Stack>
          <Stack flex={1}>
            <Typography variant="subtitle1" sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
              {attendancesDetailPageI18n('temp_name', { name: student?.nickName })}
            </Typography>
            <Typography variant="caption" sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
              {student?.nameWithFallback}
            </Typography>
          </Stack>
        </Stack>

        <form onSubmit={handleSubmit(editAttendanceHandler)} style={{ width: '100%' }}>
          <Stack gap={3}>
            <Stack gap={1}>
              <Controller
                key="startOperationTime"
                control={control}
                name="startOperationTime"
                render={({ field: { value, ...field }, fieldState: { error, invalid } }) => (
                  <Stack flexDirection="row" gap={1} alignItems="baseline">
                    <Typography variant="body1" sx={{ flexShrink: 0 }}>
                      {attendancesDetailPageI18n('clock_in_time_label')}:{' '}
                    </Typography>

                    <Stack flexDirection="row" gap={1} alignItems="baseline" flexWrap="wrap">
                      <TimePickerWithShorthand
                        key={pickerKey}
                        {...field}
                        readOnly={!editable}
                        value={value ? dayjs(value) : null}
                        onChange={(time) => timeChangeHandler('startOperationTime', time)}
                        desktopModeMediaQuery="@media (max-width: 1px)"
                        slotProps={{
                          textField: {
                            error: invalid,
                            helperText: error?.message,
                            InputProps: {
                              size: 'small',
                              endAdornment: <Schedule />,
                              sx: { maxWidth: 160, textAlign: 'left' },
                            },
                          },
                        }}
                      />

                      {isStartOperationInRange ? null : (
                        <Typography variant="caption" color="error">
                          ({attendancesDetailPageI18n('clock_in_location_out_of_range')})
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                )}
              />
              <Stack width={240} maxWidth="100%" marginLeft={1}>
                <Card variant="outlined" sx={{ aspectRatio: 1 }}>
                  {attendance.startOperationPhoto ? (
                    <EnlargeableAvatar src={attendance.startOperationPhoto} fullWidth fullHeight noBorderRadius />
                  ) : (
                    <Stack width="100%" height="100%" padding={1} justifyContent="center" alignItems="center">
                      <Person sx={{ color: theme.palette.text.secondary, fontSize: 40 }} />
                      <Typography variant="caption" color={theme.palette.text.secondary} textAlign="center">
                        {attendancesDetailPageI18n('clock_in_out_photo_placeholder')}
                        <br />
                        {attendancesDetailPageI18n('clock_in_photo_caption')}
                      </Typography>
                    </Stack>
                  )}
                </Card>
              </Stack>
            </Stack>
            <Stack gap={1}>
              <Controller
                key="endOperationTime"
                control={control}
                name="endOperationTime"
                render={({ field: { value, ...field }, fieldState: { error, invalid } }) => (
                  <Stack flexDirection="row" gap={1} alignItems="baseline">
                    <Typography variant="body1" sx={{ flexShrink: 0 }}>
                      {attendancesDetailPageI18n('clock_out_time_label')}:{' '}
                    </Typography>

                    <Stack flexDirection="row" gap={1} alignItems="baseline" flexWrap="wrap">
                      <TimePickerWithShorthand
                        key={pickerKey}
                        {...field}
                        readOnly={!editable}
                        value={value ? dayjs(value) : null}
                        onChange={(time) => timeChangeHandler('endOperationTime', time)}
                        desktopModeMediaQuery="@media (max-width: 1px)"
                        slotProps={{
                          textField: {
                            error: invalid,
                            helperText: error?.message,
                            InputProps: {
                              size: 'small',
                              endAdornment: <Schedule />,
                              sx: { maxWidth: 160, textAlign: 'left' },
                            },
                          },
                        }}
                      />

                      {isEndOperationInRange ? null : (
                        <Typography variant="caption" color="error">
                          ({attendancesDetailPageI18n('clock_out_location_out_of_range')})
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                )}
              />

              <Stack width={240} maxWidth="100%" marginLeft={1}>
                <Card variant="outlined" sx={{ aspectRatio: 1 }}>
                  {attendance.endOperationPhoto ? (
                    <EnlargeableAvatar src={attendance.endOperationPhoto} fullWidth fullHeight noBorderRadius />
                  ) : (
                    <Stack width="100%" height="100%" padding={1} justifyContent="center" alignItems="center">
                      <Person sx={{ color: theme.palette.text.secondary, fontSize: 40 }} />
                      <Typography variant="caption" color={theme.palette.text.secondary} textAlign="center">
                        {attendancesDetailPageI18n('clock_in_out_photo_placeholder')}
                        <br />
                        {attendancesDetailPageI18n('clock_out_photo_caption')}
                      </Typography>
                    </Stack>
                  )}
                </Card>
              </Stack>
            </Stack>
            <Controller
              key="breakDurationMinute"
              control={control}
              name="breakDurationMinute"
              render={({ field: { value, onChange, ...field }, fieldState: { error, invalid } }) => (
                <Stack gap={1} flexDirection="row" alignItems="baseline">
                  <Typography variant="body1">{attendancesDetailPageI18n('break_duration_label')}: </Typography>
                  <NumberTextField
                    {...field}
                    size="small"
                    value={value ?? ''}
                    onNumberChange={onChange}
                    placeholder={attendancesDetailPageI18n('break_duration_placeholder')}
                    error={invalid}
                    helperText={error?.message}
                    InputProps={{
                      readOnly: !editable,
                      fullWidth: true,
                      inputProps: { pattern: '[0-9]*' },
                      endAdornment: (
                        <InputAdornment position="end">{attendancesDetailPageI18n('minutes_unit')}</InputAdornment>
                      ),
                      sx: { maxWidth: 160, textAlign: 'left' },
                    }}
                  />
                </Stack>
              )}
            />

            <Stack gap={1}>
              <Typography variant="body1">{attendancesDetailPageI18n('time_summary_label')}:</Typography>
              <Stack gap={0.5} paddingLeft={2}>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {attendancesDetailPageI18n('job_group_label')}
                </Typography>
                <Typography variant="body2">
                  {attendancesDetailPageI18n('job_start_at_label')}:{' '}
                  {job?.jobStartAt ? formatTime(job.jobStartAt) : '-'}
                </Typography>
                <Typography variant="body2">
                  {attendancesDetailPageI18n('job_end_at_label')}: {job?.jobEndAt ? formatTime(job.jobEndAt) : '-'}
                </Typography>
                <Typography variant="body2">
                  {attendancesDetailPageI18n('job_break_duration_label')}:{' '}
                  {isNil(job?.breakDurationMinute)
                    ? '-'
                    : attendancesDetailPageI18n('job_break_duration_value', { count: job?.breakDurationMinute })}
                </Typography>
                <Typography variant="body2">
                  {attendancesDetailPageI18n('job_duration_label')}:{' '}
                  {isNil(jobDurationHours)
                    ? '-'
                    : attendancesDetailPageI18n('job_duration_value', { count: jobDurationHours })}
                </Typography>
                <Typography variant="body2">
                  {attendancesDetailPageI18n('ot_threshold_label')}:{' '}
                  {isNil(jobOtHour) ? '-' : attendancesDetailPageI18n('ot_threshold_value', { count: jobOtHour })}
                </Typography>
              </Stack>
              <Stack gap={0.5} paddingLeft={2}>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {attendancesDetailPageI18n('attendance_group_label')}
                </Typography>
                <Typography variant="body2">
                  {attendancesDetailPageI18n('operation_duration_label')}:{' '}
                  {isNil(operationDurationHours)
                    ? '-'
                    : attendancesDetailPageI18n('operation_duration_value', { count: operationDurationHours })}
                </Typography>
                <Typography variant="body2">
                  {attendancesDetailPageI18n('ot_duration_label')}:{' '}
                  {isNil(operationDurationOtHours)
                    ? '-'
                    : attendancesDetailPageI18n('ot_duration_value', {
                        count: operationDurationOtHours,
                        threshold: jobOtHour,
                      })}
                </Typography>
              </Stack>
            </Stack>

            <Stack paddingTop={1} paddingBottom={2} alignItems="flex-end">
              {updateErrorMessage ? (
                <Typography variant="body2" color="error">
                  {updateErrorMessage}
                </Typography>
              ) : null}
              {editable ? (
                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!isDirty || isSubmitting}
                  loading={updateLoading}
                >
                  {attendancesDetailPageI18n('save_changes_button')}
                </LoadingButton>
              ) : null}
            </Stack>
          </Stack>
        </form>
      </ContainerFlexHeight>
    );
  },
);

const AttendancesDetailPageContainer = () => {
  const { id: attendanceId } = useParams();
  const { currentAttendance, currentAttendanceId, setCurrentAttendanceId, setDetailPageDirty, selectAttendance } =
    React.useContext(AttendancesPageContext);

  // Sync currentAttendanceId in context when navigating via deep link
  React.useEffect(() => {
    if (attendanceId && currentAttendanceId !== attendanceId) {
      setCurrentAttendanceId(attendanceId);
    }
  }, [attendanceId, setCurrentAttendanceId]);

  // Clear dirty flag when unmounting or when attendanceId changes
  React.useEffect(
    () => () => {
      setDetailPageDirty(false);
    },
    [attendanceId],
  );

  return (
    <AttendancesDetailPage
      attendance={isNil(attendanceId) ? null : currentAttendance}
      setHasUnsavedChanges={setDetailPageDirty}
      selectAttendance={selectAttendance}
    />
  );
};

export default AttendancesDetailPageContainer;
